import { fontSize, fontWeight, letterSpacing } from '@/src/core/utils/typography';
import { Ionicons } from '@expo/vector-icons';
import { Image, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { PostWithDetails } from '../../domain/models';
import { useTranslation } from '../../i18n';

export interface PostCardProps {
  post: PostWithDetails;
  currentUserId?: string;
  onUserPress: (userId: string) => void;
  onTournamentPress?: (tournamentId: string) => void;
  onDeletePress?: (postId: string) => void;
  onLikePress?: (postId: string, isLiked: boolean) => void;
  onSharePress?: (postId: string) => void;
  colors: {
    card: string;
    border: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    divider: string;
    error: string;
    primary: string;
    shadow: string;
  };
}

export const PostCard = ({
  post,
  currentUserId,
  onUserPress,
  onTournamentPress,
  onDeletePress,
  onLikePress,
  onSharePress,
  colors,
}: PostCardProps) => {
  const { t } = useTranslation();
  const isOwnPost = currentUserId === post.userId;
  const displayName = post.userName || post.userEmail || t('misc.unknownUser');

  // Format timestamp (e.g., "2 hours ago", "3 days ago")
  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 7) {
      return date.toLocaleDateString();
    } else if (days > 0) {
      return t('time.daysAgo', { count: days });
    } else if (hours > 0) {
      return t('time.hoursAgo', { count: hours });
    } else if (minutes > 0) {
      return t('time.minutesAgo', { count: minutes });
    } else {
      return t('time.justNow');
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.card, borderColor: colors.border, shadowColor: colors.shadow }]}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity style={styles.userInfo} onPress={() => onUserPress(post.userId)}>
          {post.userAvatarUrl ? (
            <Image source={{ uri: post.userAvatarUrl }} style={styles.avatar} />
          ) : (
            <View style={[styles.avatarPlaceholder, { backgroundColor: colors.border }]}>
              <Ionicons name="person" size={20} color={colors.textSecondary} />
            </View>
          )}
          <View>
            <Text style={[styles.userName, { color: colors.text }]}>{displayName}</Text>
            <Text style={[styles.timestamp, { color: colors.textTertiary }]}>
              {formatTimestamp(post.createdAt)}
            </Text>
          </View>
        </TouchableOpacity>

        {isOwnPost && onDeletePress && (
          <TouchableOpacity
            onPress={() => onDeletePress(post.id)}
            style={styles.menuButton}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Ionicons name="trash-outline" size={20} color={colors.error} />
          </TouchableOpacity>
        )}
      </View>

      {/* Content */}
      <Text style={[styles.content, { color: colors.text }]}>{post.content}</Text>

      {/* Image */}
      {post.imageUrl && (
        <Image
          source={{ uri: post.imageUrl }}
          style={styles.image}
          resizeMode="cover"
        />
      )}

      {/* Actions row: tournament badge (left) + like & share (right) */}
      {(onLikePress || (onSharePress && post.tournamentId && post.tournamentIsPublic) || (post.tournamentId && post.tournamentName && onTournamentPress)) && (
        <View style={[styles.actionsRow, { borderTopColor: colors.divider }]}>
          {post.tournamentId && post.tournamentName && onTournamentPress && (
            <TouchableOpacity
              style={[styles.tournamentBadge, { backgroundColor: colors.border }]}
              onPress={() => onTournamentPress(post.tournamentId!)}
            >
              <Ionicons name="trophy-outline" size={16} color={colors.primary} />
              <Text style={[styles.tournamentName, { color: colors.text }]} numberOfLines={1}>
                {post.tournamentName}
              </Text>
            </TouchableOpacity>
          )}
          <View style={styles.actionsRowSpacer} />
          {onLikePress && (
            <TouchableOpacity
              style={styles.likeButton}
              onPress={() => onLikePress(post.id, post.isLikedByUser)}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons
                name={post.isLikedByUser ? 'heart' : 'heart-outline'}
                size={22}
                color={post.isLikedByUser ? colors.error : colors.textSecondary}
              />
              {post.likeCount > 0 && (
                <Text style={[styles.likeCount, { color: colors.textSecondary }]}>
                  {post.likeCount}
                </Text>
              )}
            </TouchableOpacity>
          )}
          {onSharePress && post.tournamentId && post.tournamentIsPublic && (
            <TouchableOpacity
              style={styles.shareButton}
              onPress={() => onSharePress(post.id)}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons name="share-social-outline" size={22} color={colors.textSecondary} />
            </TouchableOpacity>
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 15,
    marginVertical: 8,
    marginHorizontal: 16,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 6,
    elevation: 2,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  userInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 10,
  },
  avatarPlaceholder: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  userName: {
    fontSize: fontSize.body,
    fontWeight: fontWeight.semibold,
    letterSpacing: letterSpacing.normal,
    marginBottom: 2,
  },
  timestamp: {
    fontSize: fontSize.sm,
  },
  menuButton: {
    padding: 4,
  },
  content: {
    fontSize: fontSize.body,
    lineHeight: 22,
    marginBottom: 12,
    maxHeight: 132, // 6 lines max (22 * 6) to prevent huge height differences
  },
  image: {
    width: '100%',
    aspectRatio: 1, // Square images (1:1)
    borderRadius: 8,
    marginBottom: 12,
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 12,
    borderTopWidth: 1,
  },
  actionsRowSpacer: {
    flex: 1,
    minWidth: 8,
  },
  likeButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  likeCount: {
    fontSize: 14,
    fontWeight: '500',
  },
  shareButton: {
    padding: 4,
    marginLeft: 8,
  },
  tournamentBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderRadius: 16,
    gap: 6,
  },
  tournamentName: {
    fontSize: fontSize.sm,
    fontWeight: fontWeight.medium,
    flexShrink: 1,
  },
});
